import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import {
  FM_CHIP_CLASS,
  FM_DATA_ATTR,
  maskFreemarker,
  roundTripsCleanly,
  unmaskFreemarker,
} from '../../core/freemarker-html-mask';
import { NotificationStarterTemplate } from '../../core/notification-starter-templates';
import {
  FREEMARKER_SNIPPETS,
  FreemarkerSnippet,
} from '../../core/freemarker-catalog';
import { validateFreemarker } from '../../core/freemarker-validate';

/** An option in the "insert variable" menu. */
export interface VisualEditorVariable {
  label: string;
  value: string;
}

/** Minimal surface of the editable iframe document we drive imperatively. */
interface EditableFrameDocument {
  body: HTMLElement;
  execCommand(commandId: string, showUI?: boolean, value?: string): boolean;
}

/** Captures the `<body>…</body>` region of a full HTML document so the visual
    editor can edit only the body while the `<head>` and doctype are preserved. */
const BODY_PATTERN = /(<body\b[^>]*>)([\s\S]*?)(<\/body\s*>)/i;

/**
 * Reusable visual (WYSIWYG) HTML editor for Freemarker email bodies.
 *
 * It renders the HTML in a sandboxed, contentEditable iframe so authors edit
 * what the email will look like — no code. Freemarker is protected with the
 * chip round-trip from {@link maskFreemarker}/{@link unmaskFreemarker}, so
 * `${var}` and directives survive editing. The component is self-contained
 * (value in/out plus variable and starter-template inputs) so it can be reused
 * anywhere an email/HTML body is edited, not just in the notification editor.
 */
@Component({
  selector: 'irembogov-visual-html-editor',
  templateUrl: './visual-html-editor.component.html',
  styleUrls: ['./visual-html-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VisualHtmlEditorComponent implements OnChanges {
  /** Freemarker + HTML source. Two-way bindable via `htmlChange`. */
  @Input() html = '';
  @Output() htmlChange = new EventEmitter<string>();

  /** Variables offered in the insert-variable menu. */
  @Input() variables: VisualEditorVariable[] = [];
  /** Variable labels surfaced first (most used) in the insert-variable menu. */
  @Input() mostUsed: string[] = [];
  /** Optional starter templates the author can drop in as a starting point. */
  @Input() starterTemplates: NotificationStarterTemplate[] = [];
  /** Stretches the editor to fill its container (e.g. host fullscreen) instead
      of using its default fixed height. */
  @Input() fill = false;

  /** Raised when the current template cannot be edited visually without risking
      Freemarker loss, so the host can steer the author to the raw HTML editor. */
  @Output() supportedChange = new EventEmitter<boolean>();

  /** Freemarker directives/expressions offered in the "Insert Freemarker" menu. */
  @Input() freemarkerSnippets: FreemarkerSnippet[] = FREEMARKER_SNIPPETS;

  frameSrcdoc: SafeHtml = '';
  supported = true;
  linkDraft = '';
  imageDraft = '';
  showLinkInput = false;
  showImageInput = false;

  /** Chip currently being edited via the popover, and its editing state. Set
      when the author clicks a Freemarker chip in the iframe; cleared on save,
      remove or cancel. */
  private editingChip: HTMLElement | null = null;
  chipDraft = '';
  chipError = '';
  /** Fixed-position coordinates (outer viewport) for the chip-edit popover. */
  chipPopoverPos: { x: number; y: number } | null = null;

  /** The raw HTML currently rendered in the iframe. Guards against re-injecting
      (and resetting the caret) when the change came from our own editing. */
  private renderedHtml: string | null = null;
  private frameDoc: EditableFrameDocument | null = null;
  /** When the value is a full HTML document, the original source is kept here so
      edits are spliced back into its `<body>` and the `<head>` is not lost. */
  private documentShell: string | null = null;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef,
    private readonly zone: NgZone
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['html'] && this.html !== this.renderedHtml) {
      this.loadHtmlIntoFrame(this.html);
    }
  }

  /** Prioritised list of variables: most-used first, then the rest. */
  orderedVariables(): VisualEditorVariable[] {
    if (!this.mostUsed.length) return this.variables;
    const rank = (variable: VisualEditorVariable): number => {
      const index = this.mostUsed.indexOf(variable.label);
      return index === -1 ? this.mostUsed.length : index;
    };
    return [...this.variables].sort((a, b) => rank(a) - rank(b));
  }

  /** True while the value has no visible content, so the host can offer starter
      templates only when there is nothing to overwrite. */
  get isEmpty(): boolean {
    const raw = (this.html || '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<script[\s\S]*?<\/script>/gi, '');
    if (/<(img|hr|table|iframe|video|svg|button|input|figure)\b/i.test(raw)) {
      return false;
    }
    const text = raw.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ');
    return text.trim().length === 0;
  }

  /** Full HTML document rendered inside the editable iframe, with the Freemarker
      masked into chips and editor styles applied. When the value is already a
      full document its own `<head>` (and therefore its CSS) is preserved so the
      editing surface renders exactly like the email; the editor chrome is only
      appended. Pure and unit-testable. */
  buildFrameDocument(rawHtml: string): string {
    const masked = maskFreemarker(rawHtml || '');
    const hasDocumentShell =
      /<html[\s>]/i.test(masked) || /<body[\s>]/i.test(masked);
    const chrome = `<base target="_blank">${this.editorStyleMarkup(
      hasDocumentShell
    )}`;
    if (!hasDocumentShell) {
      return `<!doctype html><html><head>${chrome}</head><body>${masked}</body></html>`;
    }
    // Append after the template's own styles so chips always win, and the
    // template's layout/typography is what the author sees while editing.
    if (/<\/head\s*>/i.test(masked)) {
      return masked.replace(/<\/head\s*>/i, `${chrome}</head>`);
    }
    if (/<head[\s>]/i.test(masked)) {
      return masked.replace(/<head([^>]*)>/i, `<head$1>${chrome}`);
    }
    if (/<html[\s>]/i.test(masked)) {
      return masked.replace(/<html([^>]*)>/i, `<html$1><head>${chrome}</head>`);
    }
    return masked.replace(/(<body\b[^>]*>)/i, `<head>${chrome}</head>$1`);
  }

  /** Serialises the current iframe body back to Freemarker + HTML and emits it.
      When the value was a full document, the edited body is spliced back into
      the original so the `<head>` survives. Separated from the DOM so it is
      unit-testable with a plain string. */
  syncFromFrame(bodyHtml: string): void {
    const restoredBody = unmaskFreemarker(bodyHtml);
    const output = this.documentShell
      ? this.documentShell.replace(
          BODY_PATTERN,
          (_match, open, _inner, close) => `${open}${restoredBody}${close}`
        )
      : restoredBody;
    this.renderedHtml = output;
    this.html = output;
    this.htmlChange.emit(output);
  }

  onFrameLoad(event: Event): void {
    const iframe = event.target as HTMLIFrameElement;
    const doc = iframe.contentDocument;
    if (!doc?.body) {
      this.setSupported(false);
      return;
    }
    doc.body.contentEditable = 'true';
    doc.body.setAttribute('spellcheck', 'true');
    doc.body.setAttribute('aria-label', 'Email body');
    // The iframe is a separate document realm Angular's zone does not patch, so
    // its input events fire outside the zone; re-enter it so the emitted change
    // triggers change detection (host validators refresh as the author types).
    doc.addEventListener('input', () =>
      this.zone.run(() => this.handleFrameInput(doc.body))
    );
    // A chip is contenteditable="false", so it can't be typed into directly.
    // Clicking one opens a host popover to edit its Freemarker source; clicking
    // anywhere else dismisses the popover.
    doc.addEventListener('click', event => {
      const target = event.target as HTMLElement | null;
      const chipEl = target?.closest?.(
        `.${FM_CHIP_CLASS}`
      ) as HTMLElement | null;
      if (chipEl) {
        event.preventDefault();
        this.zone.run(() => this.openChipEditor(chipEl, iframe));
      } else if (this.editingChip) {
        this.zone.run(() => this.closeChipEditor());
      }
    });
    this.frameDoc = doc as unknown as EditableFrameDocument;
  }

  private handleFrameInput(body: HTMLElement): void {
    this.syncFromFrame(body.innerHTML);
  }

  exec(command: string, value?: string): void {
    if (!this.frameDoc) return;
    this.frameDoc.body.focus();
    this.frameDoc.execCommand(command, false, value);
    this.handleFrameInput(this.frameDoc.body);
  }

  formatBlock(tag: string): void {
    this.exec('formatBlock', tag);
  }

  insertVariable(value: string): void {
    if (!value || !this.frameDoc) return;
    this.frameDoc.body.focus();
    // Insert as plain `${var}` text so authors see and can edit it like any
    // other text — interpolations survive the round trip without a chip.
    this.frameDoc.execCommand('insertText', false, value);
    this.handleFrameInput(this.frameDoc.body);
  }

  /** Inserts a catalog snippet at the caret. Directives/comments go in as chips
      (masked) so they survive; a bare interpolation goes in as editable text. */
  insertFreemarker(snippet: string): void {
    if (!snippet || !this.frameDoc) return;
    this.frameDoc.body.focus();
    const masked = maskFreemarker(snippet);
    if (masked === snippet) {
      this.frameDoc.execCommand('insertText', false, snippet);
    } else {
      this.frameDoc.execCommand('insertHTML', false, masked);
    }
    this.handleFrameInput(this.frameDoc.body);
  }

  /** True while a chip is being edited (drives the popover in the template). */
  get isEditingChip(): boolean {
    return this.editingChip !== null;
  }

  /** Opens the popover to edit a clicked chip's Freemarker source, positioned
      just below the chip. */
  private openChipEditor(chip: HTMLElement, iframe: HTMLIFrameElement): void {
    const chipRect = chip.getBoundingClientRect();
    const frameRect = iframe.getBoundingClientRect();
    this.editingChip = chip;
    this.chipDraft = chip.getAttribute(FM_DATA_ATTR) ?? '';
    this.chipError = '';
    this.chipPopoverPos = {
      x: frameRect.left + chipRect.left,
      y: frameRect.top + chipRect.bottom + 4,
    };
    this.cdr.markForCheck();
  }

  /** Validation for a single chip's draft. Block-balance is intentionally NOT
      checked here — a chip holds one tag (half a pair), so its balance is only
      meaningful against the whole document (the host validation panel does
      that). Only self-contained mistakes are flagged. */
  chipDraftError(): string {
    const raw = this.chipDraft.trim();
    if (!raw) return 'Enter a Freemarker expression or directive.';
    const problem = validateFreemarker(raw).find(
      item =>
        item.message.includes('expression') || item.message.includes('comment')
    );
    return problem?.message ?? '';
  }

  /** Applies the edited source back to the chip, re-masking so the label,
      tooltip and kind stay consistent. Editing a directive into a plain `${…}`
      turns the chip back into editable text. */
  saveChipEdit(): void {
    const error = this.chipDraftError();
    if (error) {
      this.chipError = error;
      this.cdr.markForCheck();
      return;
    }
    const chip = this.editingChip;
    if (chip && chip.ownerDocument && this.frameDoc) {
      const holder = chip.ownerDocument.createElement('span');
      holder.innerHTML = maskFreemarker(this.chipDraft.trim());
      chip.replaceWith(...Array.from(holder.childNodes));
      this.handleFrameInput(this.frameDoc.body);
    }
    this.closeChipEditor();
  }

  /** Deletes the chip being edited. */
  removeChip(): void {
    const chip = this.editingChip;
    if (chip && this.frameDoc) {
      chip.remove();
      this.handleFrameInput(this.frameDoc.body);
    }
    this.closeChipEditor();
  }

  closeChipEditor(): void {
    this.editingChip = null;
    this.chipDraft = '';
    this.chipError = '';
    this.chipPopoverPos = null;
    this.cdr.markForCheck();
  }

  toggleLinkInput(): void {
    this.showLinkInput = !this.showLinkInput;
    this.showImageInput = false;
  }

  toggleImageInput(): void {
    this.showImageInput = !this.showImageInput;
    this.showLinkInput = false;
  }

  applyLink(): void {
    const url = this.linkDraft.trim();
    if (url) this.exec('createLink', url);
    this.linkDraft = '';
    this.showLinkInput = false;
  }

  applyImage(): void {
    const url = this.imageDraft.trim();
    if (url && this.frameDoc) {
      this.frameDoc.body.focus();
      this.frameDoc.execCommand(
        'insertHTML',
        false,
        `<img src="${url.replace(
          /"/g,
          '&quot;'
        )}" alt="" style="max-width:100%;height:auto" />`
      );
      this.handleFrameInput(this.frameDoc.body);
    }
    this.imageDraft = '';
    this.showImageInput = false;
  }

  applyStarter(id: string): void {
    const template = this.starterTemplates.find(item => item.id === id);
    if (!template) return;
    this.loadHtmlIntoFrame(template.content);
    this.renderedHtml = template.content;
    this.html = template.content;
    this.htmlChange.emit(template.content);
  }

  private loadHtmlIntoFrame(rawHtml: string): void {
    const source = rawHtml || '';
    this.renderedHtml = rawHtml;
    this.setSupported(roundTripsCleanly(source));
    // A full document is rendered whole so its <head> CSS styles the editing
    // surface; only its <body> is contentEditable and spliced back on save.
    const match = BODY_PATTERN.exec(source);
    this.documentShell = match ? source : null;
    this.frameSrcdoc = this.sanitizer.bypassSecurityTrustHtml(
      this.buildFrameDocument(source)
    );
    this.cdr.markForCheck();
  }

  private setSupported(supported: boolean): void {
    if (this.supported === supported) return;
    this.supported = supported;
    this.supportedChange.emit(supported);
    this.cdr.markForCheck();
  }

  /** Editor chrome injected into the iframe. For a full document only the chip
      pills and a caret reset are added so the template's own CSS is untouched
      and the surface matches the real email; a bare fragment additionally gets
      readable defaults (font, padding) so it is comfortable to edit. */
  private editorStyleMarkup(hasDocumentShell: boolean): string {
    const chip = [
      'body[contenteditable]{outline:none;}',
      'img{max-width:100%;}',
      '.fm-chip{display:inline-block;padding:0 6px;margin:0 1px;border-radius:6px;',
      'font:600 12px/1.6 "SFMono-Regular",Menlo,Consolas,monospace;',
      'background:#eff4ff;color:#155eef;border:1px solid #d1e0ff;white-space:nowrap;',
      'user-select:none;cursor:pointer;}',
      '.fm-chip:hover{filter:brightness(0.96);box-shadow:0 0 0 2px rgba(21,94,239,0.15);}',
      '.fm-chip--directive{background:#fdf2fa;color:#c11574;border-color:#fcceee;}',
      '.fm-chip--comment{background:#f2f4f7;color:#667085;border-color:#eaecf0;}',
    ];
    const fragment = [
      'html,body{margin:0;max-width:100%;overflow-x:hidden;}',
      'body{font-family:Arial,Helvetica,sans-serif;color:#344054;line-height:1.5;',
      'padding:12px;min-height:360px;word-break:break-word;}',
      '*{box-sizing:border-box;}',
      'img{height:auto;}',
      'table{max-width:100%;}',
    ];
    const rules = hasDocumentShell ? chip : [...fragment, ...chip];
    return `<style>${rules.join('')}</style>`;
  }
}
